import { appStorage } from './appStorage.util';

/**
 * Auth storage key - defined here to avoid circular dependency with useStorage.util.ts
 * Must match StorageKey.AUTH in useStorage.util.ts
 */
const AUTH_STORAGE_KEY = 'chariot.auth';

/**
 * Custom event name for tenant change notifications.
 * Dispatched when impersonation starts or stops to notify useStorage hooks
 * to re-compute their storage keys with the new tenant context.
 */
export const TENANT_CHANGE_EVENT = 'chariot:tenant-changed';

/**
 * Storage key for the currently active tenant (impersonated user).
 * This is set during impersonation and cleared when stopping impersonation.
 * Takes precedence over auth.me when computing tenant-scoped storage keys.
 */
export const CURRENT_TENANT_KEY = 'chariot.currentTenant';

/**
 * Storage keys that should be scoped to the current tenant.
 * These are filter-related keys that could leak data between tenants
 * if not properly isolated.
 *
 * Note: These values must match the corresponding StorageKey enum values
 * in useStorage.util.ts. We use string literals here to avoid circular imports.
 */
export const TENANT_SCOPED_FILTER_KEYS: string[] = [
  'filters.assets.v1', // StorageKey.FILTERS_ASSETS
  'filters.assets.v2', // StorageKey.FILTERS_ASSETS_V2
  'filters.assets.v3', // StorageKey.FILTERS_ASSETS_V3
  'filters.graphExplorer.v1', // StorageKey.FILTERS_GRAPH_EXPLORER
  'filters.risks', // StorageKey.FILTERS_RISKS
  'filters.technologies', // StorageKey.FILTERS_TECHNOLOGIES
  'filters.seeds', // StorageKey.FILTERS_SEEDS
  'filters.customerManagement.v1', // StorageKey.FILTERS_CUSTOMER_MANAGEMENT
];

/**
 * Dispatch a tenant change event to notify all useStorage hooks.
 * Call this whenever the active tenant changes (impersonation start/stop).
 *
 * The event causes useStorage hooks to re-compute their storage keys
 * with the new tenant context, ensuring proper data isolation.
 */
export function dispatchTenantChange(): void {
  window.dispatchEvent(new CustomEvent(TENANT_CHANGE_EVENT));
}

/**
 * Set the current tenant ID for impersonation.
 * This takes precedence over auth.me when computing tenant-scoped storage keys.
 *
 * Call this when starting impersonation, BEFORE dispatching the tenant change event.
 *
 * @param tenantId - The tenant ID (email) of the impersonated user
 */
export function setCurrentTenant(tenantId: string): void {
  appStorage.setItem(CURRENT_TENANT_KEY, tenantId);
}

/**
 * Clear the current tenant ID, ending impersonation.
 * After clearing, getCurrentTenantId will fall back to auth.me.
 *
 * Call this when stopping impersonation, BEFORE dispatching the tenant change event.
 */
export function clearCurrentTenant(): void {
  appStorage.removeItem(CURRENT_TENANT_KEY);
}

/**
 * Get the current tenant ID for storage key scoping.
 *
 * Priority order:
 * 1. Synced tenant (set during impersonation via setCurrentTenant)
 * 2. auth.me (the logged-in user's email)
 * 3. 'default' fallback
 *
 * This ensures that during impersonation, storage is scoped to the
 * impersonated user, not the logged-in user.
 *
 * @returns The tenant ID (email) or 'default' if not available
 */
export function getCurrentTenantId(): string {
  // Primary: Check for synced tenant (impersonation)
  const currentTenant = appStorage.getItem<string>(CURRENT_TENANT_KEY);
  if (currentTenant) {
    return currentTenant;
  }

  // Fallback: Read from auth.me (logged-in user)
  const auth = appStorage.getItem<{ me: string }>(AUTH_STORAGE_KEY);
  return auth?.me || 'default';
}

/**
 * Generate a tenant-scoped storage key.
 *
 * Format: {tenantId}:{baseKey}
 *
 * Example: "user@example.com:filters.assets.v1"
 *
 * @param baseKey - The base storage key (e.g., 'filters.assets.v1')
 * @returns The tenant-scoped key
 */
export function getTenantScopedKey(baseKey: string): string {
  const tenantId = getCurrentTenantId();
  return `${tenantId}:${baseKey}`;
}

/**
 * Check if a storage key should be tenant-scoped.
 *
 * Only filter-related keys need tenant scoping to prevent
 * cross-tenant data leakage.
 *
 * @param key - The storage key to check
 * @returns True if the key should be tenant-scoped
 */
export function isTenantScopedFilterKey(key: string): boolean {
  return TENANT_SCOPED_FILTER_KEYS.includes(key);
}

/**
 * Migrate a legacy (unscoped) storage key to tenant-scoped format.
 *
 * This function handles the one-time migration of existing filter data
 * from the old global format to the new tenant-scoped format.
 *
 * Migration behavior:
 * 1. If no legacy key exists, returns undefined
 * 2. If tenant-scoped key already exists, returns undefined (preserves new data)
 * 3. If legacy key exists but no scoped key, migrates data and returns the value
 *
 * Note: The legacy key is NOT removed to support a grace period where
 * users may switch between old and new versions of the application.
 *
 * @param baseKey - The base storage key to migrate
 * @returns The migrated value, or undefined if no migration was needed
 */
export function migrateLegacyKey<T>(baseKey: string): T | undefined {
  // Check if tenant-scoped key already exists
  const scopedKey = getTenantScopedKey(baseKey);
  const existingScopedValue = appStorage.getItem<T>(scopedKey);

  // If scoped key exists, don't overwrite it
  if (existingScopedValue !== undefined) {
    return undefined;
  }

  // Check for legacy unscoped key
  const legacyValue = appStorage.getItem<T>(baseKey);

  if (legacyValue === undefined) {
    return undefined;
  }

  // Migrate: Copy legacy value to tenant-scoped key
  appStorage.setItem(scopedKey, legacyValue);

  // Return the migrated value so caller can use it immediately
  return legacyValue;
}

/**
 * Get a tenant-scoped value, with automatic migration of legacy keys.
 *
 * This is a convenience function that:
 * 1. Attempts to migrate any legacy unscoped key
 * 2. Returns the value from the tenant-scoped key
 *
 * @param baseKey - The base storage key
 * @returns The value from tenant-scoped storage, or undefined
 */
export function getTenantScopedValue<T>(baseKey: string): T | undefined {
  // Attempt migration first (no-op if already migrated or no legacy key)
  migrateLegacyKey<T>(baseKey);

  // Return value from tenant-scoped key
  const scopedKey = getTenantScopedKey(baseKey);
  return appStorage.getItem<T>(scopedKey);
}

/**
 * Set a tenant-scoped value.
 *
 * @param baseKey - The base storage key
 * @param value - The value to store
 */
export function setTenantScopedValue<T>(baseKey: string, value: T): void {
  const scopedKey = getTenantScopedKey(baseKey);
  appStorage.setItem(scopedKey, value);
}

/**
 * Remove a tenant-scoped value.
 *
 * @param baseKey - The base storage key
 */
export function removeTenantScopedValue(baseKey: string): void {
  const scopedKey = getTenantScopedKey(baseKey);
  appStorage.removeItem(scopedKey);
}
